export function isImage(file) {
  const fileName = file.name || file.path;
  const suffix = fileName.substr(fileName.indexOf('.') + 1).toLowerCase();
  if (suffix === 'jpg' || suffix === 'jpeg' || suffix === 'bmp' || suffix === 'png') {
    return true;
  }
  return false;
}

export const changeDateToSampleFormat = (myDate) => {
  const date = new Date(myDate);
  date.setDate(date.getDate() + 1);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

/** MM/DD/YYYY for @leapeasy/ui-kit DatePickerInput */
export const formatDateForPicker = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

/** API ISO (YYYY-MM-DD) or existing MM/DD/YYYY → DatePickerInput value */
export const formatIsoDateForPicker = (value) => {
  if (value == null || value === '') {
    return '';
  }
  if (value instanceof Date) {
    return formatDateForPicker(value);
  }
  if (typeof value !== 'string') {
    return '';
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }
  if (trimmed.includes('/')) {
    return trimmed;
  }
  const datePart = trimmed.split('T')[0];
  const [year, month, day] = datePart.split('-');
  if (!year || !month || !day) {
    return trimmed;
  }
  return `${month}/${day}/${year}`;
};

export const parsePickerDate = (value) => {
  if (!value || typeof value !== 'string') {
    return null;
  }
  const parts = value.trim().split('/');
  if (parts.length !== 3) {
    return null;
  }
  const [month, day, year] = parts.map((part) => Number(part));
  if (!month || !day || !year) {
    return null;
  }
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
};

export const isValidPickerDate = (value) => parsePickerDate(value) !== null;

export const isDateOnOrBefore = (value, maxDate) => {
  const parsed = parsePickerDate(value);
  if (!parsed || !(maxDate instanceof Date)) {
    return false;
  }
  const end = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());
  return parsed <= end;
};

export const isDateOnOrAfter = (value, minDate) => {
  const parsed = parsePickerDate(value);
  if (!parsed || !(minDate instanceof Date)) {
    return false;
  }
  const start = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
  return parsed >= start;
};

/** SF aggregate landlord queries return Name flat; some payloads use Landlord__r.Name */
export const getLandlordDisplayName = (landlord) =>
  landlord?.Landlord__r?.Name || landlord?.Name || '';

/** Resident DOB from SF Contact or user-edited value → DatePickerInput value */
export const getTenantDobValue = (tenant) => {
  const raw = tenant?.Date_of_Birth__c_changed || tenant?.Date_of_Birth__c;
  if (!raw) {
    return '';
  }
  return formatIsoDateForPicker(String(raw));
};

const MIN_BIRTH_DATE = new Date(1900, 0, 1);

/** Latest allowed DOB — today (residents may include minors on multi-resident policies). */
export const getMaxDateForDob = () => {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth(), today.getDate());
};

export const validateTenantDob = (value, maxBirthDate = getMaxDateForDob()) => {
  if (!value) {
    return 'Please select a birthday';
  }
  if (!isValidPickerDate(value)) {
    return 'Please enter a valid date (MM/DD/YYYY)';
  }
  if (!isDateOnOrAfter(value, MIN_BIRTH_DATE)) {
    return 'Please enter a valid birthday';
  }
  if (!isDateOnOrBefore(value, maxBirthDate)) {
    return 'Date of birth cannot be in the future';
  }
  return '';
};

export const areAllTenantDobsValid = (tenants, maxBirthDate = getMaxDateForDob()) => {
  if (!Array.isArray(tenants) || tenants.length === 0) {
    return false;
  }
  return tenants.every((tenant) => !validateTenantDob(getTenantDobValue(tenant), maxBirthDate));
};

/** Resident names synced from portal policy row (Tenant_*__r.Name). */
export const getExpectedResidentNamesFromPrefill = (data) =>
  ['tenant_1_name', 'tenant_2_name', 'tenant_3_name', 'tenant_4_name']
    .map((key) => (typeof data?.[key] === 'string' ? data[key].trim() : ''))
    .filter(Boolean);
